import { AxiosResponse } from "axios";
import authModule, { TokenResource } from "../../../utilities/AuthModule";
import { axiosRetryGet } from "../../../utilities/AxiosRetryUtil";
import { logger } from "../../../utilities/logger";
import { SharePointSiteHelper } from "../../../utilities/SharePointSiteHelper";
import { buildClientListKey, buildListEndpoint, extractServerRelativeListUrl, normalizeClientListWebUrl } from "./clientListUrl";
import {
  buildClientTypeItemsUrl,
  CLIENT_LIST_REQUEST_TIMEOUT_MS,
  ClientTypeLookup,
  IClientTypeColumns,
  interpretClientTypeRows,
  resolveClientTypeColumns,
} from "./clientTypeQuery";
import { getFieldsByListEndpoint } from "./SharePointRestServices";

const UNKNOWN: ClientTypeLookup = Object.freeze({ state: "unknown" });

// Keyed by normalized client list url + the EXTRACTED SharePoint site id - not the composite:
// every web in a site collection shares that guid and therefore the same client row, so a
// subsite is answered from cache rather than issuing an identical second request. Client type
// is a property of the site collection and every web beneath it inherits it. The url is part
// of the key so a tenant-setting edit that repoints the client list mid-session cannot be
// answered from the old list's verdicts.
const lookupCache: Map<string, ClientTypeLookup> = new Map<string, ClientTypeLookup>();
// The pending promise per lookup key. Several components mount in the same render (five
// metadata inputs plus panels is the normal case); without this each would fire an identical
// request before the first result lands - "one request per site" must hold for CONCURRENT
// callers, not just sequential ones.
const inFlight: Map<string, Promise<ClientTypeLookup>> = new Map<string, Promise<ClientTypeLookup>>();
const columnsCache: Map<string, IClientTypeColumns> = new Map<string, IClientTypeColumns>();
const columnsInFlight: Map<string, Promise<IClientTypeColumns | undefined>> = new Map<string, Promise<IClientTypeColumns | undefined>>();
// Lists whose schema read was refused (401/403) or not found (404): this user cannot read them, or
// they are not there, and asking again this session would only be refused again - and logged again,
// once per lookup (production log 2026-10-26).
const unreadableLists: Set<string> = new Set<string>();
const warnedLists: Set<string> = new Set<string>();
// Bumped by clearClientTypeCache. A resolution that was already in flight when the caches were
// cleared (account switch during a lookup) must NOT write its result - fetched under the
// previous account's authorization - into the freshly cleared caches when it settles. Every
// cache write checks the generation it started under.
let cacheGeneration: number = 0;

export const clearClientTypeCache = (): void => {
  cacheGeneration += 1;
  lookupCache.clear();
  inFlight.clear();
  columnsCache.clear();
  columnsInFlight.clear();
  unreadableLists.clear();
  warnedLists.clear();
};

const lookupKey = (clientListUrl: string, siteId: string): string =>
  `${clientListUrl.trim().toLowerCase()}|${siteId}`;

/** Whether this session's schema read found the list unreadable for this user, or not there.
 * Every client-list feature is then off until the next session or account. */
export const isClientListUnreadable = (clientListUrl?: string): boolean => {
  const listKey: string | undefined = clientListUrl?.trim() ? buildClientListKey(clientListUrl) : undefined;
  return !!listKey && unreadableLists.has(listKey);
};

const isUnreadable = (error: any): boolean => {
  const status: any = error?.response?.status;
  return status === 401 || status === 403 || status === 404;
};

/** The cached answer for a site, or undefined when it has not been resolved yet. Synchronous,
 * so a render path can consult it without awaiting. */
export const peekClientType = (clientListUrl?: string, compositeSiteId?: string): ClientTypeLookup | undefined => {
  const siteId: string | undefined = SharePointSiteHelper.extractSharePointSiteId(compositeSiteId);
  return siteId && clientListUrl?.trim() ? lookupCache.get(lookupKey(clientListUrl, siteId)) : undefined;
};

const warnOnce = (startedGeneration: number, warnKey: string, message: string, error?: any): void => {
  // Generation-guarded like every other module-level write: a flight settling after an
  // account-switch clear must not log the OLD account's failure into the new session, nor
  // pre-suppress the new account's first legitimate warning for the list.
  if (cacheGeneration !== startedGeneration || warnedLists.has(warnKey)) {
    return;
  }
  warnedLists.add(warnKey);
  logger(error ?? message, `clientTypeService.ts (${message})`);
};

const resolveColumns = async (
  accessToken: string,
  webUrl: string,
  listEndpoint: string,
  listKey: string,
  startedGeneration: number,
): Promise<IClientTypeColumns | undefined> => {
  const cached: IClientTypeColumns | undefined = columnsCache.get(listKey);
  if (cached) {
    return cached;
  }
  if (unreadableLists.has(listKey)) {
    return undefined;
  }

  // Shared while in flight: the inactive-client read at session start and the first site's
  // lookups can ask at the same moment. Identity-checked removal, as for inFlight below.
  const pending: Promise<IClientTypeColumns | undefined> | undefined = columnsInFlight.get(listKey);
  if (pending) {
    return pending;
  }
  const read: Promise<IClientTypeColumns | undefined> = resolveColumnsUncached(accessToken, webUrl, listEndpoint, listKey, startedGeneration);
  columnsInFlight.set(listKey, read);
  try {
    return await read;
  } finally {
    if (columnsInFlight.get(listKey) === read) {
      columnsInFlight.delete(listKey);
    }
  }
};

const resolveColumnsUncached = async (
  accessToken: string,
  webUrl: string,
  listEndpoint: string,
  listKey: string,
  startedGeneration: number,
): Promise<IClientTypeColumns | undefined> => {
  let fields: { InternalName: string, Title: string, TypeAsString?: string }[];
  try {
    fields = await getFieldsByListEndpoint(accessToken, webUrl, listEndpoint, CLIENT_LIST_REQUEST_TIMEOUT_MS, { rethrow: true });
  } catch (error) {
    // Logged by getFieldsByListEndpoint. A refused or missing list is remembered for the session;
    // any other failure (throttling, a server error, a timeout) is not, so the next lookup
    // re-attempts - one transient failure must not disable the feature for the session. Nor is a
    // 401 while the SharePoint token held had expired: through an outage the add-in keeps the
    // sign-in and hands that token out (audit 2026-10-27). Not without one, or without its expiry
    // (review 2026-10-28: each lookup asked again, and logged, as before a8bbdb48a).
    const expiredToken: boolean = error?.response?.status === 401 && authModule.hasTokenExpired(TokenResource.SharePoint);
    if (isUnreadable(error) && !expiredToken && cacheGeneration === startedGeneration) {
      unreadableLists.add(listKey);
    }
    return undefined;
  }
  // NEVER cache a resolution derived from an empty or malformed field list: it would log a
  // misleading "no resolvable column" warning for a healthy list. Returning undefined makes the
  // caller yield `unknown` WITHOUT caching, so the next lookup re-attempts.
  if (!Array.isArray(fields) || fields.length === 0) {
    return undefined;
  }

  const columns: IClientTypeColumns = resolveClientTypeColumns(fields);
  if (cacheGeneration === startedGeneration) {
    columnsCache.set(listKey, columns);
  }

  if (!columns.siteIdInternal) {
    warnOnce(startedGeneration, `${listKey}:siteid`, `client list '${listKey}' has no resolvable SiteId column; client-type restrictions cannot be applied`);
  } else if (!columns.typeInternal) {
    warnOnce(startedGeneration, `${listKey}:type`, `client list '${listKey}' has no resolvable client type column; client-type restrictions cannot be applied`);
  }

  return columns;
};

/** Where a client list lives and what its columns are called, resolved once per session per
 * list. Every client-list feature (client type, client groups) starts from this so the one
 * fields request is shared. Undefined for an unusable url, a list this user cannot read
 * (isClientListUnreadable; not asked again this session) or a failed or empty schema read; the
 * last is deliberately not cached so a later call re-attempts. */
export interface IClientListContext {
  webUrl: string;
  listEndpoint: string;
  listKey: string;
  columns: IClientTypeColumns;
}

export const resolveClientListContext = async (clientListUrl?: string): Promise<IClientListContext | undefined> => {
  const webUrl: string | undefined = clientListUrl?.trim() ? normalizeClientListWebUrl(clientListUrl) : undefined;
  if (!webUrl) {
    return undefined;
  }
  const listEndpoint: string = buildListEndpoint(extractServerRelativeListUrl(clientListUrl));
  // The same key the client group session copy is cached under, so its synchronous peek finds it.
  const listKey: string = buildClientListKey(clientListUrl);
  const startedGeneration: number = cacheGeneration;
  try {
    const accessToken: string = await authModule.getSharePointAccessToken();
    const columns: IClientTypeColumns | undefined = await resolveColumns(accessToken, webUrl, listEndpoint, listKey, startedGeneration);
    return columns ? { webUrl, listEndpoint, listKey, columns } : undefined;
  } catch (error) {
    warnOnce(startedGeneration, `${listKey}:schema`, `client list schema lookup failed for client list '${listKey}'`, error);
    return undefined;
  }
};

/**
 * The client type of a single site, or `unknown` when it cannot be determined. One request per
 * site collection - shared by concurrent callers - cached for the session. Every failure yields
 * `unknown`, which makes the restriction inert - a transient SharePoint problem must never
 * silently strip datasource-backed inputs out of a user's form.
 */
export const resolveClientType = async (clientListUrl: string, compositeSiteId: string): Promise<ClientTypeLookup> => {
  const siteId: string | undefined = SharePointSiteHelper.extractSharePointSiteId(compositeSiteId);
  if (!siteId || !clientListUrl?.trim()) {
    return UNKNOWN;
  }

  const cacheKey: string = lookupKey(clientListUrl, siteId);
  const cached: ClientTypeLookup | undefined = lookupCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const pending: Promise<ClientTypeLookup> | undefined = inFlight.get(cacheKey);
  if (pending) {
    return pending;
  }

  const lookup: Promise<ClientTypeLookup> = resolveClientTypeUncached(clientListUrl, siteId, cacheKey, cacheGeneration);
  inFlight.set(cacheKey, lookup);
  try {
    return await lookup;
  } finally {
    // Identity-checked: a clear during the flight may have emptied the map and a NEW caller
    // may have registered a fresh entry under the same key - deleting that one would break
    // its dedup.
    if (inFlight.get(cacheKey) === lookup) {
      inFlight.delete(cacheKey);
    }
  }
};

const resolveClientTypeUncached = async (
  clientListUrl: string,
  siteId: string,
  cacheKey: string,
  startedGeneration: number,
): Promise<ClientTypeLookup> => {
  const webUrl: string | undefined = normalizeClientListWebUrl(clientListUrl);
  if (!webUrl) {
    return UNKNOWN;
  }

  const listEndpoint: string = buildListEndpoint(extractServerRelativeListUrl(clientListUrl));
  const listKey: string = buildClientListKey(clientListUrl);

  try {
    const accessToken: string = await authModule.getSharePointAccessToken();
    const columns: IClientTypeColumns | undefined = await resolveColumns(accessToken, webUrl, listEndpoint, listKey, startedGeneration);
    if (!columns) {
      // The schema read failed or came back empty, or the list cannot be read (see
      // resolveColumns). Deliberately NOT cached here; a repeat visit or a later site re-attempts,
      // without a request for a list this session found unreadable.
      return UNKNOWN;
    }

    const url: string | undefined = buildClientTypeItemsUrl(webUrl, listEndpoint, columns, siteId);
    if (!url) {
      // A deterministic miss from a real, non-empty schema (no queryable column): cache it so
      // the whole session stops asking.
      if (cacheGeneration === startedGeneration) {
        lookupCache.set(cacheKey, UNKNOWN);
      }
      return UNKNOWN;
    }

    const response: AxiosResponse<any> = await axiosRetryGet(url, {
      headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json;odata=nometadata" },
      timeout: CLIENT_LIST_REQUEST_TIMEOUT_MS,
    });

    const result: ClientTypeLookup = interpretClientTypeRows(response?.data?.value, columns.typeInternal);
    if (result.state === "unknown") {
      // Malformed payload or conflicting duplicate rows. Not cached - the malformed case is
      // transient, and the two are indistinguishable here. Logged once per list so ambiguous
      // client-list data is discoverable.
      warnOnce(startedGeneration, `${listKey}:rows`, `client type lookup for client list '${listKey}' returned an unusable result (malformed payload or conflicting duplicate rows)`);
      return result;
    }
    if (cacheGeneration === startedGeneration) {
      lookupCache.set(cacheKey, result);
    }
    return result;
  } catch (error) {
    // Deliberately NOT cached: a threshold error or a network blip would otherwise disable the
    // dimension for the rest of the session. The warn-once guard keeps a persistently broken
    // list from flooding the log, and only a newly visited site re-attempts.
    warnOnce(startedGeneration, `${listKey}:read`, `client type lookup failed for client list '${listKey}'`, error);
    return UNKNOWN;
  }
};
